// MarketMovers.js — Ultra-compact 3-block strip
import React, { useState, useEffect } from 'react';
import { TrendingUp, TrendingDown, Activity, RefreshCw } from 'lucide-react';

const API_URL = process.env.REACT_APP_API_URL || 'https://financial-analyst-backend-production-7175.up.railway.app/api';

const BLOCKS = [
  { key: 'gainers', label: 'Top Gainers', Icon: TrendingUp,   color: '#22c55e', dim: 'rgba(34,197,94,0.1)',   border: 'rgba(34,197,94,0.18)'  },
  { key: 'losers',  label: 'Top Losers',  Icon: TrendingDown, color: '#ef4444', dim: 'rgba(239,68,68,0.1)',   border: 'rgba(239,68,68,0.18)'  },
  { key: 'active',  label: 'Most Active', Icon: Activity,     color: '#f59e0b', dim: 'rgba(245,158,11,0.1)',  border: 'rgba(245,158,11,0.18)' },
];

const MarketMovers = ({ setActiveTab, onSelectStock }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const fetch_ = async (isRefresh = false) => {
    isRefresh ? setRefreshing(true) : setLoading(true);
    try {
      const res = await fetch(`${API_URL}/market/movers`);
      if (!res.ok) throw new Error();
      setData(await res.json());
    } catch { setData(null); }
    finally { setLoading(false); setRefreshing(false); }
  };

  useEffect(() => { fetch_(); }, []);

  const handleStock = (sym) => { if (onSelectStock) onSelectStock(sym); else if (setActiveTab) setActiveTab('company'); };

  return (
    <div style={{ margin: '0.5rem 0 1rem' }}>
      {/* Wrapper: 3-col grid on desktop, horizontal scroll on mobile */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(3, 1fr)',
        gap: '0.625rem',
      }}
        className="mv-grid"
      >
        {BLOCKS.map(b => (
          <Block key={b.key} b={b}
            stocks={data?.[b.key] || []}
            loading={loading} refreshing={refreshing}
            onRefresh={() => fetch_(true)} onStock={handleStock}
          />
        ))}
      </div>
      <style>{`
        @keyframes mspin { to { transform: rotate(360deg); } }
        @media (max-width: 640px) {
          .mv-grid {
            display: flex !important;
            flex-direction: row !important;
            overflow-x: auto !important;
            gap: 0.5rem !important;
            padding-bottom: 0.25rem;
            -webkit-overflow-scrolling: touch;
            scrollbar-width: none;
          }
          .mv-grid::-webkit-scrollbar { display: none; }
          .mv-grid > * {
            min-width: 160px !important;
            flex-shrink: 0 !important;
          }
        }
      `}</style>
    </div>
  );
};

const Block = ({ b, stocks, loading, refreshing, onRefresh, onStock }) => {
  const neg = b.key === 'losers';
  const rowColor = (s) => b.key === 'active' ? (s.changePercent >= 0 ? '#22c55e' : '#ef4444') : b.color;

  return (
    <div style={{ background: 'rgba(15,23,42,0.75)', border: '1px solid #1e293b', borderRadius: '0.75rem', overflow: 'hidden' }}>
      {/* Header — single tight row */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0.4rem 0.75rem', background: b.dim, borderBottom: `1px solid ${b.border}` }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.3rem' }}>
          <b.Icon size={11} color={b.color} />
          <span style={{ fontSize: '0.68rem', fontWeight: '700', color: b.color }}>{b.label}</span>
        </div>
        <button onClick={onRefresh} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#475569', padding: 0, display: 'flex' }}>
          <RefreshCw size={10} style={{ animation: refreshing ? 'mspin 1s linear infinite' : 'none' }} />
        </button>
      </div>

      {/* Rows */}
      {loading && [1,2,3,4,5].map(i => (
        <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '0.3rem 0.75rem', opacity: 0.25 }}>
          <div style={{ width: 36, height: 8, background: '#1e293b', borderRadius: 2 }} />
          <div style={{ width: 44, height: 8, background: '#1e293b', borderRadius: 2 }} />
        </div>
      ))}

      {!loading && (!stocks || !stocks.length) && (
        <div style={{ padding: '0.75rem', textAlign: 'center', fontSize: '0.65rem', color: '#475569' }}>
          No data · <button onClick={onRefresh} style={{ color: b.color, background: 'none', border: 'none', cursor: 'pointer', fontSize: '0.65rem' }}>retry</button>
        </div>
      )}

      {!loading && stocks.map((s, i) => (
        <Row key={s.symbol + i} s={s} color={rowColor(s)} last={i === stocks.length - 1} onClick={() => onStock(s.symbol)} />
      ))}
    </div>
  );
};

const Row = ({ s, color, last, onClick }) => {
  const [hov, setHov] = useState(false);
  const sign = s.changePercent >= 0 ? '+' : '';
  return (
    <button
      onClick={onClick}
      onMouseEnter={() => setHov(true)} onMouseLeave={() => setHov(false)}
      style={{
        width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '0.28rem 0.75rem',
        background: hov ? 'rgba(30,41,59,0.6)' : 'transparent',
        border: 'none', borderBottom: last ? 'none' : '1px solid rgba(30,41,59,0.4)',
        cursor: 'pointer', transition: 'background 0.1s',
        WebkitTapHighlightColor: 'transparent',
      }}
    >
      <span style={{ fontSize: '0.72rem', fontWeight: '700', color: '#e2e8f0' }}>{s.symbol}</span>
      <div style={{ textAlign: 'right' }}>
        <span style={{ fontSize: '0.65rem', color: '#64748b', marginRight: '0.3rem' }}>${s.price.toFixed(2)}</span>
        <span style={{ fontSize: '0.68rem', fontWeight: '700', color }}>{sign}{s.changePercent.toFixed(2)}%</span>
      </div>
    </button>
  );
};

export default MarketMovers;